import React from "react";
import { ListChildComponentProps } from "react-window";
import { HighlightedText } from "../searchUtils";
import {
  ColumnRange,
  GridSelection,
  isCellSelected,
  ROW_INDEX_WIDTH,
} from "./gridGeometry";
import { SheetColumn, SheetRow } from "./sheetTypes";

/**
 * One grid row. It lives at module scope and is memoised on purpose: when the
 * row component is declared inside the grid it gets a new identity on every
 * render, and React unmounts and remounts every visible row instead of
 * updating it - which is what made scrolling and filtering feel heavy.
 */

export interface RowMouseHandlers {
  onCellDown: (rowPos: number, colPos: number, extend: boolean) => void;
  onCellEnter: (rowPos: number, colPos: number, buttons: number) => void;
  onGutterDown: (rowPos: number, extend: boolean) => void;
  onRevealRowsBefore: (rowPos: number) => void;
  onCellExpand: (rowPos: number, colPos: number, anchor: HTMLElement) => void;
}

export interface RowItemData extends RowMouseHandlers {
  rows: SheetRow[];
  columns: SheetColumn[];
  widths: number[];
  offsets: number[];
  totalWidth: number;
  range: ColumnRange;
  /** Columns hidden immediately before the visible column at this position. */
  hiddenBefore: number[];
  /** Leading columns that stay put while the rest scrolls. */
  frozenCount: number;
  /** Rows hidden immediately before the row at this position, by position. */
  hiddenRowsBefore: Record<number, number>;
  query: string;
  activeMatch: { rowPos: number; colPos: number } | null;
  selection: GridSelection | null;
}

const CELL_BASE: React.CSSProperties = {
  flexShrink: 0,
  padding: "4px 8px",
  fontSize: 12,
  borderRight: "1px solid #eee",
  boxSizing: "border-box",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const SheetGridRow: React.FC<ListChildComponentProps<RowItemData>> = ({
  index,
  style,
  data,
}) => {
  const {
    rows,
    columns,
    widths,
    offsets,
    totalWidth,
    range,
    hiddenBefore,
    frozenCount,
    hiddenRowsBefore,
    query,
    activeMatch,
    selection,
    onCellDown,
    onCellEnter,
    onGutterDown,
    onRevealRowsBefore,
    onCellExpand,
  } = data;

  const row = rows[index];
  if (!row) return null;

  const isEven = index % 2 === 0;
  const isActiveRow = activeMatch != null && activeMatch.rowPos === index;
  const hiddenAbove = hiddenRowsBefore[index] ?? 0;

  const rowBackground = isActiveRow
    ? "rgba(255,152,0,0.10)"
    : isEven
      ? "#fff"
      : "#fafafa";

  const frozen = Math.min(Math.max(frozenCount, 0), columns.length);
  const windowFirst = Math.max(frozen, range.first);
  const windowLast = Math.min(columns.length - 1, range.last);

  const renderCell = (c: number, isFrozen: boolean) => {
    const column = columns[c];
    const raw = row.cells[column.sourceIndex];
    const value = raw == null ? "" : raw;
    const isActiveCell = isActiveRow && activeMatch!.colPos === c;
    const selected = isCellSelected(selection, index, c);

    // Scrolling cells pass underneath a frozen one, so its background can never
    // be transparent - it takes the row's own.
    const background = isActiveCell
      ? "rgba(255,152,0,0.25)"
      : selected
        ? "rgba(25,118,210,0.12)"
        : isFrozen
          ? rowBackground
          : undefined;

    return (
      <div
        key={column.id}
        role="gridcell"
        aria-colindex={c + 1}
        style={{
          ...CELL_BASE,
          width: widths[c],
          // A double left border is where a hidden column used to be.
          borderLeft: hiddenBefore[c] > 0 ? "3px double #f0ad4e" : undefined,
          borderRight:
            isFrozen && c === frozen - 1
              ? "2px solid #bdbdbd"
              : CELL_BASE.borderRight,
          backgroundColor: background,
          outline: isActiveCell ? "2px solid #ff9800" : undefined,
          outlineOffset: isActiveCell ? "-2px" : undefined,
          cursor: "cell",
          position: isFrozen ? "sticky" : undefined,
          left: isFrozen ? ROW_INDEX_WIDTH + offsets[c] : undefined,
          zIndex: isFrozen ? 1 : undefined,
        }}
        title={value}
        onMouseDown={(event) => {
          if (event.button !== 0) return;
          event.preventDefault();
          // The second press of a double click, read here rather than through
          // onDoubleClick: the first press re-renders the row, and the pair did
          // not always survive that to become a dblclick event.
          if (event.detail >= 2) {
            onCellExpand(index, c, event.currentTarget);
            return;
          }
          onCellDown(index, c, event.shiftKey);
        }}
        onMouseEnter={(event) => onCellEnter(index, c, event.buttons)}
      >
        {query ? <HighlightedText text={value} query={query} /> : value}
      </div>
    );
  };

  const cells: React.ReactNode[] = [];

  for (let c = 0; c < frozen; c++) cells.push(renderCell(c, true));

  const lead = offsets[windowFirst] - offsets[frozen];
  if (lead > 0) {
    cells.push(<div key="lead" style={{ width: lead, flexShrink: 0 }} />);
  }

  for (let c = windowFirst; c <= windowLast; c++) {
    cells.push(renderCell(c, false));
  }

  if (windowLast >= 0 && windowLast < columns.length - 1) {
    const tail = offsets[columns.length] - offsets[windowLast + 1];
    if (tail > 0) {
      cells.push(<div key="tail" style={{ width: tail, flexShrink: 0 }} />);
    }
  }

  return (
    <div
      role="row"
      aria-rowindex={row.sourceIndex + 1}
      style={{
        ...style,
        display: "flex",
        width: totalWidth,
        backgroundColor: isActiveRow
          ? "rgba(255,152,0,0.10)"
          : isEven
            ? "#fff"
            : "#fafafa",
        borderBottom: "1px solid #eee",
        borderTop: hiddenAbove > 0 ? "3px solid #f0ad4e" : undefined,
      }}
    >
      <div
        style={{
          width: ROW_INDEX_WIDTH,
          flexShrink: 0,
          padding: "4px 8px",
          fontSize: 12,
          fontWeight: 700,
          textAlign: "center",
          color: "#666",
          borderRight: "1px solid #eee",
          backgroundColor:
            selection && isRowInSelection(selection, index)
              ? "#e3f2fd"
              : "#f5f5f5",
          position: "sticky",
          left: 0,
          zIndex: 2,
          boxSizing: "border-box",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          cursor: "pointer",
        }}
        title={
          hiddenAbove > 0
            ? `${hiddenAbove} hidden row${hiddenAbove === 1 ? "" : "s"} above. Click the marker to show them.`
            : `Row ${row.sourceIndex + 1} in the file`
        }
        onMouseDown={(event) => {
          if (event.button !== 0) return;
          event.preventDefault();
          onGutterDown(index, event.shiftKey);
        }}
      >
        {hiddenAbove > 0 && (
          <span
            role="button"
            aria-label={`Show ${hiddenAbove} hidden rows`}
            style={{
              float: "left",
              color: "#b26a00",
              cursor: "pointer",
              fontSize: 11,
              lineHeight: "18px",
            }}
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onRevealRowsBefore(index);
            }}
          >
            {"⇅"}
          </span>
        )}
        {row.sourceIndex + 1}
      </div>
      {cells}
    </div>
  );
};

function isRowInSelection(selection: GridSelection, rowPos: number): boolean {
  const top = Math.min(selection.anchorRow, selection.focusRow);
  const bottom = Math.max(selection.anchorRow, selection.focusRow);
  return rowPos >= top && rowPos <= bottom;
}

export default React.memo(SheetGridRow);
